import { useAppSelector } from 'app/hooks';
import axios from 'axios';
import { SystemOptionType } from 'data/types';
import { useEffect, useMemo, useRef, useState } from 'react';

type ProductEntry = { productId: number; price: number };

export function getSystemProducts(system: SystemOptionType): ProductEntry[] {
  const products: ProductEntry[] = [];
  system.configuration.condensers.forEach((condenser) => {
    if (condenser.selectedProduct?.productId) {
      products.push({
        productId: condenser.selectedProduct.productId,
        price: condenser.selectedProduct.price ?? 0
      });
    }
    condenser.zones.forEach((zone) => {
      if (zone.selectedProduct?.productId) {
        products.push({
          productId: zone.selectedProduct.productId,
          price: zone.selectedProduct.price ?? 0
        });
      }
    });
  });
  return products;
}

type RebateResponseEntry = {
  id: number | string;
  rebateMaximum?: number;
  programs?: unknown[];
};

export function useRebateEligibleProductIds(
  systems: SystemOptionType[]
): Set<number> {
  const locationData = useAppSelector((state) => state.project.locationData);
  const zipcode = locationData?.zipcode;

  const uniqueProducts = useMemo(() => {
    const priceByProductId = new Map<number, number>();
    systems.forEach((system) => {
      getSystemProducts(system).forEach(({ productId, price }) => {
        if (!priceByProductId.has(productId)) {
          priceByProductId.set(productId, price);
        }
      });
    });
    return Array.from(priceByProductId.entries()).map(([productId, price]) => ({
      productId,
      price
    }));
  }, [systems]);

  const requestKey = useMemo(() => {
    const ids = uniqueProducts.map((p) => p.productId).sort((a, b) => a - b);
    return `${zipcode ?? ''}|${ids.join(',')}`;
  }, [uniqueProducts, zipcode]);

  const [eligibleProductIds, setEligibleProductIds] = useState<Set<number>>(
    () => new Set()
  );
  const lastRequestKeyRef = useRef<string | null>(null);

  useEffect(() => {
    if (!zipcode || uniqueProducts.length === 0) {
      return undefined;
    }
    if (lastRequestKeyRef.current === requestKey) {
      return undefined;
    }
    lastRequestKeyRef.current = requestKey;

    let active = true;
    const payload = {
      zipCode: zipcode,
      productsData: uniqueProducts.map((p) => ({
        product_id: p.productId,
        product_price: p.price
      }))
    };

    axios
      .post(
        `${process.env.REACT_APP_PARTNERNET_API_URL}/v1/api/upfront/get_rebates`,
        payload
      )
      .then((res) => {
        if (!active) return;
        const rebates = (res?.data?.data ?? []) as RebateResponseEntry[];
        const ids = new Set<number>();
        rebates.forEach((rebate) => {
          const hasOffers =
            Number(rebate.rebateMaximum) > 0 ||
            (Array.isArray(rebate.programs) && rebate.programs.length > 0);
          if (hasOffers) {
            ids.add(Number(rebate.id));
          }
        });
        setEligibleProductIds(ids);
      })
      .catch(() => {
        if (active) setEligibleProductIds(new Set());
      });

    return () => {
      active = false;
    };
  }, [requestKey, zipcode, uniqueProducts]);

  return eligibleProductIds;
}
